import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { regenAnchor, type AnchorRegenResponse } from './api';

/**
 * Screen-2 anchor-regen mutation hook.
 *
 * Fires `POST /api/book/orders/:orderId/anchor/regen` with the
 * envelope's current `cover.attemptId`. On success the server creates
 * a new AnchorAttempt at attemptNo+1 and transitions the order
 * `anchor_review → anchor_pending`. The heading customers see under
 * anchor_pending is either the initial "Wir zeichnen dein Titelbild."
 * (state 1, when remainingAnchor === 2) or "Wir zeichnen neu."
 * (state 5, when remainingAnchor < 2 — Sebastian ruling 2026-08-05 §2).
 *
 * Same discriminated-result shape as `use-anchor-accept` so the
 * caller can branch on the error's `error_code` inline (state 6 vs
 * state 7) without waiting for a re-render.
 */

export type AnchorRegenResult =
  | { readonly ok: true; readonly response: AnchorRegenResponse }
  | { readonly ok: false; readonly error: ApiError | null };

export interface UseAnchorRegenState {
  readonly regenerating: boolean;
  readonly error: ApiError | null;
  readonly regen: (anchorAttemptId: string) => Promise<AnchorRegenResult>;
}

export function useAnchorRegen(orderId: string): UseAnchorRegenState {
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const regen = useCallback(
    async (anchorAttemptId: string): Promise<AnchorRegenResult> => {
      setRegenerating(true);
      setError(null);
      try {
        const response = await regenAnchor(orderId, anchorAttemptId);
        return { ok: true, response };
      } catch (err) {
        const apiError = err instanceof ApiError ? err : null;
        setError(apiError);
        return { ok: false, error: apiError };
      } finally {
        setRegenerating(false);
      }
    },
    [orderId],
  );

  return { regenerating, error, regen };
}
